/**
 * History import (ADR-001 §10.1 "two more importers"; prototype histModal / hiTask / hiInc): old tasks, or old
 * escalations and mess-ups, from a sheet, so the record starts from the past. Managers only.
 *
 * Same pipeline as the usage report upload: the browser asks for a slot (POST /tasks/history-import/sign), sends
 * the file straight to object storage (the original is kept), then POST /tasks/history-import with dry_run=true
 * has the WORKER parse it (never the HTTP request: up to 5 000 rows) into staged rows. GET
 * /tasks/history-import/{id} is the per-row preview (Ready, or why not). POST with dry_run=false writes every ready
 * row in one transaction; error rows are left out. Limits: 10 MB, 5 000 rows, .xlsx .csv .tsv .txt, first sheet only.
 *
 * Columns (headers matched loosely, as the prototype does):
 *  - tasks:     Task, Client, SPOC, Created on, Due on, Closed on, Status. A row with a Closed on date, or a status of
 *               Closed / Done / Complete, lands finished, its proof pre-checked ("Imported from old records").
 *  - incidents: What happened, Type, Client, Owner, Raised on, Closed on, Severity, Caused by, What went wrong,
 *               Outcome. Each lands in the owner's appraisal record (incidents); a row of type escalation that
 *               names a client also opens (or records, closed) an escalation.
 * Every task, incident and escalation written carries source 'history' (the screen shows "Imported" / "How it got
 * here: Imported from old records"; raised_by is the manager who applied the sheet). The escalation a row opens is
 * never auto-synced into a second incident: the row's own incident is the one record. Dates may be "12 Sep", 12/09/2026 (day first),
 * 2026-09-12 or an Excel serial number. A client or person is resolved to an id or the row is an error: free-text
 * names are never stored.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './common.js';
import { ClientName, Severity } from './requests.js';
import { UPLOAD_EXTENSIONS, UPLOAD_MAX_BYTES } from './usage.js';
import { UserRef } from './users.js';
/** The two sheet variants. `incidents` = "Old escalations and mess-ups" in the screen. */
export const HistoryImportKind = z.enum(['tasks', 'incidents']).meta({ id: 'HistoryImportKind' });
/** Rows per file; more is refused at parse with parse_error set (status failed). */
export const HISTORY_IMPORT_MAX_ROWS = 5000;
/** The appraisal-record kinds (db incidents_kind_check), as the Type column is read. */
export const IncidentKind = z.enum(['escalation', 'missed', 'late', 'slip', 'complaint', 'closure', 'mistake']).meta({ id: 'IncidentKind' });
/** Caused by: a person, process, vendor, client, none, or unclear when the cell says nothing usable. */
export const IncidentCause = z.enum(['person', 'process', 'vendor', 'client', 'none', 'unclear']).meta({ id: 'IncidentCause' });
const hasExt = (f) => UPLOAD_EXTENSIONS.some((e) => f.toLowerCase().endsWith(e));
/** POST /tasks/history-import/sign — an upload slot. 422 over 10 MB or not .xlsx .csv .tsv .txt. */
export const SignHistoryImportBody = z
    .object({
    kind: HistoryImportKind,
    filename: z.string().trim().min(5).max(200).refine(hasExt, 'Upload an .xlsx, .csv, .tsv or .txt file'),
    bytes: z.number().int().positive().max(UPLOAD_MAX_BYTES, 'The file is over 10 MB. Split it or remove extra sheets.'),
    content_type: z.string().max(120),
})
    .strict();
/**
 * POST /tasks/history-import. `import_id` is the `upload_id` from sign (the upload key).
 * dry_run=true (default): parse in the worker. Idempotent: while parsing or once staged it answers the current state.
 * dry_run=false: write every ready row. 409 unless staged; 422 when no row is ready.
 */
export const RunHistoryImportBody = z
    .object({
    import_id: Uuid,
    dry_run: z.boolean().default(true),
})
    .strict();
export const HistoryImportIdParams = z.object({ id: Uuid });
/** awaiting_file → parsing → staged → applied; failed = the file could not be read (parse_error says why). */
export const HistoryImportStatus = z.enum(['awaiting_file', 'parsing', 'staged', 'applied', 'failed']).meta({ id: 'HistoryImportStatus' });
/**
 * Why a row is not ready. missing_title: no Task / What happened. unknown_person: the SPOC / Owner is not a
 * current ops person. unknown_client / ambiguous_client: no client, or more than one, by that name.
 * date_not_understood: a date cell that is not blank and not a date (for incidents, Raised on is required).
 */
export const HistoryRowErrorReason = z
    .enum(['missing_title', 'unknown_person', 'unknown_client', 'ambiguous_client', 'date_not_understood'])
    .meta({ id: 'HistoryRowErrorReason' });
/** A tasks-sheet row as it will be written. */
export const HistoryTaskParsed = z
    .object({
    title: z.string(),
    /** null = internal work (the Client cell was blank). */
    client: ClientName.nullable(),
    assignee: UserRef.nullable(),
    created_on: IsoDate.nullable(),
    /** Due on, else the close date, else yesterday (prototype hiTask: a row with no date at all is dated -1). */
    due_on: IsoDate.nullable(),
    closed_on: IsoDate.nullable(),
    /** Closed on set, or Status closed / done / complete / finished. Finished rows land with proof pre-checked. */
    done: z.boolean(),
})
    .meta({ id: 'HistoryTaskParsed' });
/** An incidents-sheet row as it will be written. */
/** Written as an incident with source 'history' (never 'live'), so appraisal counts can label or exclude it. */
export const HistoryIncidentParsed = z
    .object({
    title: z.string(),
    kind: IncidentKind,
    /** null = internal (no client). */
    client: ClientName.nullable(),
    /** On whose watch. */
    owner: UserRef.nullable(),
    raised_on: IsoDate.nullable(),
    closed_on: IsoDate.nullable(),
    severity: Severity,
    cause: IncidentCause,
    /** Set when cause is person: the named person, else the owner. */
    caused_by: UserRef.nullable(),
    what_went_wrong: z.string().nullable(),
    outcome: z.string().nullable(),
    /** kind escalation with a client: an escalation (source history) is written too, and not synced into a second incident. */
    makes_escalation: z.boolean(),
})
    .meta({ id: 'HistoryIncidentParsed' });
export const HistoryImportRow = z
    .object({
    /** 1-based, counting the header as row 1 (what the person sees in the sheet). */
    row_number: z.number().int().positive(),
    /** The row as read from the file, keyed by its header. */
    raw: z.record(z.string(), z.string()),
    status: z.enum(['ready', 'error']),
    error: z.object({ reason: HistoryRowErrorReason, detail: z.string() }).nullable(),
    /** Set for kind tasks (also on an error row, as far as it was understood). */
    task: HistoryTaskParsed.nullable(),
    /** Set for kind incidents. */
    incident: HistoryIncidentParsed.nullable(),
    /** After apply: the task id, or the incident id, written from this row. */
    written_id: Uuid.nullable(),
})
    .meta({ id: 'HistoryImportRow' });
export const HistoryImport = z
    .object({
    id: Uuid,
    kind: HistoryImportKind,
    original_filename: z.string(),
    bytes: z.number().int(),
    uploaded_by: UserRef,
    uploaded_at: Timestamp,
    status: HistoryImportStatus,
    rows_total: z.number().int().nonnegative(),
    rows_ready: z.number().int().nonnegative(),
    rows_error: z.number().int().nonnegative(),
    parse_error: z.string().nullable(),
    applied_at: Timestamp.nullable(),
    applied_by: UserRef.nullable(),
    /** What apply wrote (all zero before). */
    written: z.object({
        tasks: z.number().int().nonnegative(),
        incidents: z.number().int().nonnegative(),
        escalations: z.number().int().nonnegative(),
    }),
})
    .meta({ id: 'HistoryImport' });
/** GET /tasks/history-import/{id}: the import and every staged row, in sheet order. */
export const HistoryImportDetail = HistoryImport.extend({ rows: z.array(HistoryImportRow) }).meta({ id: 'HistoryImportDetail' });
//# sourceMappingURL=history-import.js.map